import { gsap } from "gsap";
import { DeviceUtil } from "../../util/device/DeviceUtil";
import { EventHandler } from "../../util/event/EventHandler";
import { Logger } from "../../util/log/Logger";
import { Delay } from "../../util/time/Delay";
import { WebUtil } from "../../util/web/WebUtil";
import { Renderer } from "../renderer/Renderer";
/**
 * FullScreenManager.ts
 */
export class FullScreenManager {
    constructor() {
        if (FullScreenManager.instance) {
            throw new Error("FullScreenManager Singleton Already Initialised");
        }
        FullScreenManager.instance = this;
    }
    getManager() {
        let manager;
        if (this.isBrimAvailable()) {
            manager = new FullScreenIOS();
        }
        else if (this.isFullScreenEnabled()) {
            manager = new FullScreenAndroid();
        }
        return manager;
    }
    isFullScreenEnabled() {
        let documentElement = document;
        return documentElement.fullscreenEnabled || documentElement.webkitFullscreenEnabled || documentElement.mozFullScreenEnabled || documentElement.msFullscreenEnabled;
    }
    isWebView() {
        let inIosApp = WebUtil.getUrlParameter("app");
        return inIosApp ? inIosApp == "ios" : false;
    }
    isBrimAvailable() {
        return !this.isWebView() && !this.isFullScreenEnabled() && DeviceUtil.isIos();
    }
    static getInstance() {
        if (FullScreenManager.instance === null) {
            FullScreenManager.instance = new FullScreenManager();
        }
        return FullScreenManager.instance;
    }
}
FullScreenManager.instance = null;
/**
 * AbstractFullScreenManager.ts
 */
export class AbstractFullScreenManager {
    start() {
    }
    open() {
    }
    exit() {
    }
    onOpen(action, scope) {
        this.openHandler = new EventHandler(action, scope);
    }
    onExit(action, scope) {
        this.exitHandler = new EventHandler(action, scope);
    }
    update() {
        let fullScreen = this.isFullScreen();
        if (fullScreen) {
            this.callHandler(this.openHandler);
        }
        else {
            this.callHandler(this.exitHandler);
        }
    }
    callHandler(handler) {
        if (handler) {
            handler.call();
        }
    }
    isFullScreen() {
        return false;
    }
    isBrim() {
        return false;
    }
}
/*
 * FullScreenAndroid.ts
 */
export class FullScreenAndroid extends AbstractFullScreenManager {
    start() {
        let handler = this.update.bind(this);
        let documentElement = document.documentElement;
        documentElement.addEventListener("fullscreenchange", handler);
        documentElement.addEventListener("webkitfullscreenchange", handler);
        documentElement.addEventListener("mozfullscreenchange", handler);
        documentElement.addEventListener("MSFullscreenChange", handler);
    }
    open() {
        if (!this.isFullScreen()) {
            let documentElement = document.documentElement;
            if (documentElement.requestFullscreen) {
                documentElement.requestFullscreen();
            }
            else if (documentElement.mozRequestFullScreen) {
                documentElement.mozRequestFullScreen();
            }
            else if (documentElement.webkitRequestFullscreen) {
                documentElement.webkitRequestFullscreen();
            }
            else if (documentElement.msRequestFullscreen) {
                documentElement.msRequestFullscreen();
            }
        }
    }
    exit() {
        if (this.isFullScreen()) {
            let documentElement = document.documentElement;
            if (documentElement.exitFullscreen) {
                documentElement.exitFullscreen();
            }
            else if (documentElement.mozCancelFullScreen) {
                documentElement.mozCancelFullScreen();
            }
            else if (documentElement.webkitExitFullscreen) {
                documentElement.webkitExitFullscreen();
            }
            else if (documentElement.msExitFullscreen) {
                documentElement.msExitFullscreen();
            }
        }
    }
    isFullScreen() {
        let documentElement = document;
        return documentElement.fullscreenElement || documentElement.mozFullScreenElement || documentElement.webkitFullscreenElement || documentElement.webkitIsFullScreen || documentElement.msFullscreenElement;
    }
}
/*
* FullScreenAnimation.ts
*/
export class FullScreenAnimation {
    constructor() {
        this.swipeHand = document.getElementById("swipeHand");
        this.brimMask = document.getElementById("brim-mask");
    }
    animate() {
        let top = parseInt(this.swipeHand.style.top);
        let isNegative = top < 0;
        let opacity = isNegative ? "0.3" : "1";
        let duration = isNegative ? 0.4 : 1;
        let yPos = isNegative ? "0px" : "-95px";
        this.swipeHand.style.opacity = opacity;
        this.tween = gsap.to(this.swipeHand, {
            top: yPos,
            duration: duration,
            onComplete: this.handleAnimateComplete,
            callbackScope: this
        });
    }
    stop() {
        if (this.tween) {
            this.tween.kill();
        }
        this.tween = null;
    }
    handleAnimateComplete() {
        if (this.brimMask.style.display != "none") {
            this.animate();
        }
        else {
            this.stop();
        }
    }
}
/*
* FullScreenIOS.ts
*/
export class FullScreenIOS extends AbstractFullScreenManager {
    start() {
        this.animation = new FullScreenAnimation();
        this.renderer = Renderer.getInstance();
        this.viewport = this.renderer.getViewport();
        let view = this.renderer.getView();
        this.brimMask = document.getElementById("brim-mask");
        this.brimMain = document.getElementById("brim-main");
        if (this.brimMain && this.brimMask) {
            this.brimMain.appendChild(view);
            this.initialiseBrim();
        }
        else {
            Logger.error("Make sure you have two dom elements at the top level of your game.html called \"brim-main\" and \"brim-mask\" or this minimal-ui thing won't work");
        }
    }
    initialiseBrim() {
        let gajus = window["gajus"];
        let screamConfig = {
            viewport: true,
            width: { portrait: screen.width, landscape: screen.height }
        };
        this.scream = gajus.Scream(screamConfig);
        this.brim = gajus.Brim({ viewport: this.scream });
        this.brim.on("viewchange", this.handleBrimViewChange.bind(this));
        this.scream.on("orientationchangeend", this.handleScreamOrientationChangeEnd.bind(this));
        window.addEventListener("orientationchange", this.handleWindowOrientationChange.bind(this));
    }
    handleBrimViewChange(event) {
        if (event.viewName == "full") {
            this.checkOrientation();
        }
        document.body.className = event.viewName;
    }
    handleScreamOrientationChangeEnd() {
        this.checkOrientation();
    }
    handleWindowOrientationChange() {
        if (this.isSameOrientation()) {
            this.callAfter(500, this.hideBrimMask, this);
        }
    }
    checkOrientation() {
        if (this.isSameOrientation()) {
            this.startAnimation();
        }
        else {
            this.callAfter(10, this.hideBrimMask, this);
        }
    }
    hideBrimMask() {
        this.brimMask.style.display = "none";
        this.forceResize();
    }
    startAnimation() {
        this.animation.animate();
    }
    forceResize() {
        this.renderer.forceResize(100);
    }
    callAfter(duration, action, scope) {
        Delay.create(duration, action, scope);
    }
    isSameOrientation() {
        let screamOrientation = this.scream.getOrientation();
        let viewportOrientation = this.viewport.getOrientation();
        return screamOrientation == viewportOrientation;
    }
    isBrim() {
        return true;
    }
}
//# sourceMappingURL=FullScreenManager.js.map